import { IonButton, IonContent, IonIcon, IonModal } from "@ionic/react";
import { arrowBack, close } from "ionicons/icons";
import { PhoneNumber } from "libphonenumber-js";
import React, { useContext, useRef, useState } from "react";
import { AppContext } from "../../../AppStateProvider";
import { useAppState } from "../../../AppListenerProvider";
import { useAuth } from "../../../AuthProvider";
import AuthService from "../../../services/auth.service";
import PreferencesService from "../../../services/preferences.service";
import UserService from "../../../services/user.service";
import OTPInput from "../../ComponentsOnboarding/OTPInput/OTPInput";
import PhoneInput from "../../ComponentsOnboarding/PhoneInput/PhoneInput";
import SparkGrid from "../../ComponentsOnboarding/SparkGrid/SparkGrid";
import WelcomeHeader from "../../ComponentsOnboarding/WelcomeHeader/WelcomeHeader";

interface PhoneVerificationModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    onVerified: () => void;
}

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({
    isOpen,
    onDismiss,
    onVerified,
}) => {
    const { state } = useContext(AppContext);
    const { isActive, isNative, platform } = useAppState();
    const { user, isLoading, isAuthenticated, firebaseApp, auth } = useAuth();
    const authService = AuthService(
        { user, isLoading, isAuthenticated, firebaseApp, auth },
        { isActive, isNative, platform }
    );

    const [isVerificationView, setIsVerificationView] = useState<boolean>(false);
    const [verificationId, setVerificationId] = useState<string>("");
    const [confirmationResult, setConfirmationResult] = useState<any>(null);
    const [phoneNumberObj, setPhoneNumberObj] = useState<PhoneNumber | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const recaptchaContainerRef = useRef<HTMLDivElement>(null);

    const resetState = () => {
        setIsVerificationView(false);
        setVerificationId("");
        setConfirmationResult(null);
        setPhoneNumberObj(null);
        setIsSubmitting(false);
        setError(null);
    };

    const handleDismiss = () => {
        resetState();
        onDismiss();
    };

    const handlePhoneSubmit = async (phoneNum: PhoneNumber) => {
        setIsSubmitting(true);
        setError(null);
        setPhoneNumberObj(phoneNum);

        try {
            const result = await authService.linkPhoneNumberWithCurrentUser(phoneNum.number);

            if (result.confirmationResult) {
                // Web flow
                setConfirmationResult(result.confirmationResult);
                setIsVerificationView(true);
            } else if (result.verificationId) {
                // Native flow
                setVerificationId(result.verificationId);
                setIsVerificationView(true);
            }
        } catch (err: any) {
            console.error("Error sending phone verification:", err);
            if (err?.code === 'auth/provider-already-linked') {
                // Phone already linked to this account — treat as verified
                await handleVerificationSuccess();
                return;
            }
            setError(err?.message || "Failed to send verification code. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOTPComplete = async (otp: string) => {
        setIsSubmitting(true);
        setError(null);

        try {
            await authService.confirmPhoneLinkCode(
                otp,
                verificationId || undefined,
                confirmationResult || undefined
            );
            await handleVerificationSuccess();
        } catch (err: any) {
            console.error("Error confirming OTP:", err);
            setError("Invalid verification code. Please try again.");
            setIsSubmitting(false);
        }
    };

    const handleVerificationSuccess = async () => {
        try {
            // Store phone number in backend
            if (phoneNumberObj && state.user?.id) {
                await UserService.verifyUserPhoneNumber(state.user.id, phoneNumberObj.number);
            }

            // Phone is verified — clear this user's "must verify" marker.
            if (user?.uid) {
                await PreferencesService.clearPhoneVerificationRequired(user.uid);
            }

            resetState();
            onVerified();
        } catch (err) {
            console.error("Error storing phone number:", err);
            // Even if backend storage fails, the phone is verified in Firebase.
            if (user?.uid) {
                await PreferencesService.clearPhoneVerificationRequired(user.uid);
            }
            resetState();
            onVerified();
        }
    };

    const handleBackToPhone = () => {
        setIsVerificationView(false);
        setVerificationId("");
        setConfirmationResult(null);
        setError(null);
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={handleDismiss}>
            <IonContent className="bg-grid-pattern">
                <div className="relative h-full w-full">
                    <SparkGrid />

                    {/* Navigation */}
                    <div className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center"
                        style={{ paddingTop: `calc(0.5rem + var(--ion-safe-area-top, 0))` }}>
                        {isVerificationView ? (
                            <IonButton fill="clear" color="theme-secondary" onClick={handleBackToPhone}>
                                <IonIcon icon={arrowBack} />
                            </IonButton>
                        ) : (
                            <div className="w-12" />
                        )}
                        <IonButton fill="clear" color="theme-secondary" onClick={handleDismiss}>
                            <IonIcon icon={close} />
                        </IonButton>
                    </div>

                    <div className="relative z-10 h-full w-full flex flex-col justify-start items-center">
                        <WelcomeHeader
                            text={isVerificationView ? "Enter verification code" : "Verify your phone number"}
                            animate={false}
                        />
                        <div className="font-bold px-4 w-full max-w-md">
                            {!isVerificationView ? (
                                <PhoneInput
                                    signUp={false}
                                    onSubmit={handlePhoneSubmit}
                                    isActive={isActive}
                                    isNative={isNative}
                                />
                            ) : (
                                <OTPInput
                                    length={6}
                                    onComplete={handleOTPComplete}
                                    signUp={false}
                                    clearError={() => setError(null)}
                                    isNative={isNative}
                                />
                            )}

                            {error && (
                                <div className="text-highlight-danger text-center text-sm mt-2">
                                    {error}
                                </div>
                            )}

                            {isSubmitting && (
                                <div className="text-primary-alt text-center text-sm mt-2">
                                    {isVerificationView ? "Verifying..." : "Sending code..."}
                                </div>
                            )}

                            {!isVerificationView && (
                                <div className="text-sm text-primary-alt text-center px-2 py-2 mt-2">
                                    A verified phone number is required to interact on Ecliptic. This helps keep our community safe.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recaptcha container for web */}
                    <div ref={recaptchaContainerRef} id="phone-verify-recaptcha" />
                </div>
            </IonContent>
        </IonModal>
    );
};

export default PhoneVerificationModal;
